import React, {useEffect, useRef} from 'react';
import {useSelector} from 'react-redux';
import {useHistory, useParams, useRouteMatch, withRouter} from 'react-router';
import {Segment} from 'semantic-ui-react';

import {withApollo} from 'react-apollo';
import {renderModalError} from '@utils/forms';
import {showErrorModal, showModal} from '@utils/modal';
import {routes} from '@constants/routes';
import {convertToInt, isUrlParamValid} from '@utils/helpers';
import {getLink} from '@appComponents/Link';
import {ClientApiKeysQuery} from '@graphql/clientAvApiKey/query';
import HeaderRenderer from '@appComponents/HeaderRenderer';
import ApiKeyIndexButtons from '@modules/client/components/ApiKey/components/ApiKeyIndexButtons';
import {loadModal} from '@modules/client/utils/apiKeyHelpers';
import useQueryCustom from '@utils/hooks/useQueryCustom';

import ApiKeyTable from '../components/ApiKey/components/ApiKeyTable';

const ApiKeyIndex = () => {
    const history = useHistory();
    const match = useRouteMatch();
    const params = useParams();
    const {bookmaker} = useSelector((state) => state.app.entities.client ?? {});
    const {isVisible} = useSelector((state) => state.modal ?? false);
    const prevModalVisibleRef = useRef(isVisible);
    const prevMatchPathRef = useRef(match.path);
    const {
        data: {clientApiKeys} = [],
        loading: isDataLoading = true,
        error,
    } = useQueryCustom({
        query: ClientApiKeysQuery,
        options: {
            variables: {
                client: convertToInt(params.id),
                fetchPolicy: 'network-only',
                notifyOnNetworkStatusChange: true,
            },
        },
    });

    useEffect(() => {
        if (error) {
            showErrorModal({
                header: 'Error',
                redirectLink: getLink(`${routes}.clients.apiKey.index`),
                text: 'There was an error while fetching the data.',
                errorButtonIcon: 'arrow circle left',
                errorButtonText: 'Back to list',
            });
        }
    }, [error]);

    useEffect(() => {
        loadModal(bookmaker, match);
    }, []);

    useEffect(() => {
        if (!isUrlParamValid(params.id)) {
            showModal({
                isVisible: true,
                content: renderModalError('Client', routes.clients.index.path),
            });
        }
    }, [params.id]);

    useEffect(() => {
        const hasMatchPathChanged = match.path !== prevMatchPathRef.current;
        const isNotIndexRoute = match.path !== routes.clients.apiKey.index.path;
        const shouldOpenModal = hasMatchPathChanged && isNotIndexRoute;

        const hasModalVisibilityChanged = isVisible !== prevModalVisibleRef.current;
        const wasModalJustClosed = hasModalVisibilityChanged && !isVisible;

        if (shouldOpenModal) {
            loadModal(bookmaker, match);
        }

        if (wasModalJustClosed) {
            history.push(getLink('clients.apiKey.index', { id: params.id }));
        }

        prevModalVisibleRef.current = isVisible;
        prevMatchPathRef.current = match.path;
    }, [match.path, isVisible, isDataLoading, bookmaker]);

    if (isDataLoading) {
        return <Segment basic loading={true}/>;
    }

    return (
        <>
            <HeaderRenderer
                buttons={ApiKeyIndexButtons}
                buttonsProps={{
                    id: convertToInt(params.id),
                    openAVClientModal: () => loadModal(bookmaker, match),
                }}
            />
            <ApiKeyTable
                clientApiKeys={clientApiKeys}
                clientId={params.id}
            />
        </>
    );
};

export default withRouter(withApollo(ApiKeyIndex));
